<script setup>
import { ref, computed, nextTick, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useMultiSelectLabel } from 'dashboard/composables/useMultiSelectLabel';
import {
  useNativeFieldOptions,
  toSlug,
} from 'dashboard/composables/useNativeFieldOptions';
import ContactAPI from 'dashboard/api/contacts';
import { useAvatarColor } from 'dashboard/composables/useAvatarColor';
import KanbanDatePicker from './KanbanDatePicker.vue';

const props = defineProps({
  card: { type: Object, required: true },
  isNew: { type: Boolean, default: false },
  value: { type: [String, Number], default: '' },
  forecast: { type: String, default: '' },
  forecastTime: { type: String, default: '' },
  assigneeId: { type: Number, default: null },
  source: { type: String, default: '' },
  columnId: { type: Number, default: null },
  pipelineId: { type: Number, default: null },
  pipelines: { type: Array, default: () => [] },
  columns: { type: Array, default: () => [] },
  agents: { type: Array, default: () => [] },
  watcherIds: { type: Array, default: () => [] },
  products: { type: Array, default: () => [] },
});
const emit = defineEmits([
  'update:value',
  'update:forecast',
  'update:forecast-time',
  'update:assignee-id',
  'update:source',
  'update:column-id',
  'update:pipeline-id',
  'update:watcher-ids',
  'update:products',
  'contactUpdated',
  'contactSelected',
  'openContact',
]);
const { avatarColor, initials: agentInitials } = useAvatarColor();

function initials(name = '') {
  return agentInitials(name) || '?';
}

function contactAvatar(c) {
  return c?.thumbnail ?? c?.avatar_url ?? null;
}

const { t } = useI18n();
const store = useStore();
const accountLabels = useMapGetter('labels/getLabels');

const liveCard = computed(
  () => store.state.kanban.cards.find(c => c.id === props.card.id) ?? props.card
);
const contact = computed(() => liveCard.value.contact ?? {});

const phone = computed(() => contact.value.phone_number ?? null);

// ── edição inline de telefone ─────────────────────────────────────────────────
const DDI_LIST = [
  { code: '+55', flag: '🇧🇷', label: 'Brasil' },
  { code: '+1', flag: '🇺🇸', label: 'EUA' },
  { code: '+351', flag: '🇵🇹', label: 'Portugal' },
  { code: '+54', flag: '🇦🇷', label: 'Argentina' },
  { code: '+34', flag: '🇪🇸', label: 'Espanha' },
  { code: '+52', flag: '🇲🇽', label: 'México' },
  { code: '+57', flag: '🇨🇴', label: 'Colômbia' },
  { code: '+56', flag: '🇨🇱', label: 'Chile' },
  { code: '+598', flag: '🇺🇾', label: 'Uruguai' },
  { code: '+595', flag: '🇵🇾', label: 'Paraguai' },
  { code: '+51', flag: '🇵🇪', label: 'Peru' },
  { code: '+44', flag: '🇬🇧', label: 'Reino Unido' },
  { code: '+49', flag: '🇩🇪', label: 'Alemanha' },
  { code: '+33', flag: '🇫🇷', label: 'França' },
  { code: '+39', flag: '🇮🇹', label: 'Itália' },
];

function extractPhoneDdiAndLocal(raw) {
  if (!raw) return { ddi: '+55', local: '' };
  const str = String(raw);
  // com espaço: "+55 51999663754"
  const matchSpaced = DDI_LIST.find(d => str.startsWith(d.code + ' '));
  if (matchSpaced)
    return {
      ddi: matchSpaced.code,
      local: str.slice(matchSpaced.code.length + 1),
    };
  // sem espaço: "+5551999663754" ou "5551999663754"
  const matchGlued = DDI_LIST.find(d => str.startsWith(d.code));
  if (matchGlued)
    return { ddi: matchGlued.code, local: str.slice(matchGlued.code.length) };
  return { ddi: '+55', local: str };
}

function formatPhoneLocal(val) {
  // aceita 10 (fixo) ou 11 (celular com 9) dígitos
  const digits = String(val).replace(/\D/g, '').slice(0, 11);
  if (!digits) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 9)
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  if (digits.length === 10)
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  // 11 dígitos: celular com nono dígito
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 3)} ${digits.slice(3, 7)}-${digits.slice(7)}`;
}

function maskPhoneInput(el) {
  const raw = el.innerText;
  const formatted = formatPhoneLocal(raw);
  if (formatted === raw) return;
  el.innerText = formatted;
  const range = document.createRange();
  const sel = window.getSelection();
  range.selectNodeContents(el);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
}

const editingPhone = ref(false);
const phoneDdi = ref('+55');
const phoneDdiOpen = ref(false);
const phoneDdiTriggerRef = ref(null);
const phoneDdiDropStyle = ref({});
const phoneEditContainerRef = ref(null);
const phoneLocalRef = ref(null);
const phoneCommitting = ref(false);
let phoneFocusOutTimer = null;

function startEditPhone() {
  const { ddi, local } = extractPhoneDdiAndLocal(phone.value);
  phoneDdi.value = ddi;
  editingPhone.value = true;
  phoneCommitting.value = false;
  nextTick(() => {
    if (phoneLocalRef.value) {
      phoneLocalRef.value.innerText = formatPhoneLocal(local);
      phoneLocalRef.value.focus();
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(phoneLocalRef.value);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  });
}

async function commitPhone() {
  if (phoneCommitting.value) return;
  phoneCommitting.value = true;
  editingPhone.value = false;
  const localDigits = (phoneLocalRef.value?.innerText?.trim() ?? '').replace(
    /\D/g,
    ''
  );
  // salva em E.164: +DDI + dígitos locais (sem formatação)
  const newPhone = localDigits ? `${phoneDdi.value}${localDigits}` : '';
  if (newPhone !== (phone.value ?? '')) {
    await ContactAPI.update(contact.value.id, {
      phone_number: newPhone || null,
    });
    store.commit('kanban/UPDATE_CARD', {
      ...liveCard.value,
      contact: { ...contact.value, phone_number: newPhone || null },
    });
  }
  phoneCommitting.value = false;
}

function onPhoneFocusIn() {
  window.clearTimeout(phoneFocusOutTimer);
}

function onPhoneFocusOut() {
  phoneFocusOutTimer = window.setTimeout(() => {
    if (!phoneDdiOpen.value) commitPhone();
  }, 150);
}

function onPhoneKeydown(e) {
  if (e.key === 'Enter') {
    e.preventDefault();
    commitPhone();
  }
  if (e.key === 'Escape') {
    phoneCommitting.value = true;
    editingPhone.value = false;
  }
  if (
    !/\d/.test(e.key) &&
    !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(
      e.key
    ) &&
    !e.ctrlKey &&
    !e.metaKey
  ) {
    e.preventDefault();
    return;
  }
  // bloqueia 12º dígito
  if (/\d/.test(e.key)) {
    const currentDigits = (e.target.innerText ?? '').replace(/\D/g, '');
    if (currentDigits.length >= 11) e.preventDefault();
  }
}

const city = computed(() => contact.value.additional_attributes?.city ?? null);
const email = computed(() => contact.value.email ?? null);
const companyName = computed(
  () =>
    contact.value.additional_attributes?.company_name ??
    contact.value.company?.name ??
    null
);

// ── edição inline de empresa ──────────────────────────────────────────────────
const editingCompany = ref(false);
const companyDraft = ref('');
const companyInputRef = ref(null);
const companyCommitting = ref(false);

function startEditCompany() {
  companyDraft.value = companyName.value ?? '';
  editingCompany.value = true;
  companyCommitting.value = false;
  nextTick(() => {
    companyInputRef.value?.focus();
    companyInputRef.value?.select();
  });
}

async function commitCompany() {
  if (companyCommitting.value) return;
  companyCommitting.value = true;
  editingCompany.value = false;
  const newCompany = companyDraft.value.trim();
  if (newCompany !== (companyName.value ?? '')) {
    const existing = contact.value.additional_attributes ?? {};
    await ContactAPI.update(contact.value.id, {
      additional_attributes: { ...existing, company_name: newCompany },
    });
    store.commit('kanban/UPDATE_CARD', {
      ...liveCard.value,
      contact: {
        ...contact.value,
        additional_attributes: { ...existing, company_name: newCompany },
      },
    });
  }
  companyCommitting.value = false;
}

function onCompanyKeydown(e) {
  if (e.key === 'Enter') {
    e.preventDefault();
    commitCompany();
  }
  if (e.key === 'Escape') {
    companyCommitting.value = true;
    editingCompany.value = false;
  }
}

// ── cidade ───────────────────────────────────────────────────────────────────
async function setCity(newCity) {
  if (newCity !== (city.value ?? '')) {
    const existing = contact.value.additional_attributes ?? {};
    await ContactAPI.update(contact.value.id, {
      additional_attributes: { ...existing, city: newCity },
    });
    store.commit('kanban/UPDATE_CARD', {
      ...liveCard.value,
      contact: {
        ...contact.value,
        additional_attributes: { ...existing, city: newCity },
      },
    });
  }
}

// ── edição inline de e-mail ───────────────────────────────────────────────────
const editingEmail = ref(false);
const emailDraft = ref('');
const emailInputRef = ref(null);
const emailCommitting = ref(false);

watch(
  email,
  v => {
    if (!editingEmail.value) emailDraft.value = v ?? '';
  },
  { immediate: true }
);

async function commitEmail() {
  if (emailCommitting.value) return;
  emailCommitting.value = true;
  editingEmail.value = false;
  const newEmail = emailDraft.value.trim();
  if (newEmail !== (email.value ?? '')) {
    await ContactAPI.update(contact.value.id, { email: newEmail || null });
    store.commit('kanban/UPDATE_CARD', {
      ...liveCard.value,
      contact: { ...contact.value, email: newEmail || null },
    });
  }
  emailCommitting.value = false;
}

function onEmailKeydown(e) {
  if (e.key === 'Enter') {
    e.preventDefault();
    commitEmail();
  }
  if (e.key === 'Escape') {
    emailCommitting.value = true;
    editingEmail.value = false;
  }
}

// ── helpers de posicionamento de dropdown ────────────────────────────────────
function fixedDropStyle(triggerEl, width = 200) {
  if (!triggerEl) return {};
  const rect = triggerEl.getBoundingClientRect();
  const margin = 8;
  const dropW = Math.max(rect.width, width);
  const left = Math.min(
    Math.max(rect.left, margin),
    window.innerWidth - dropW - margin
  );
  const goUp = rect.bottom + 240 > window.innerHeight;
  return {
    position: 'fixed',
    left: `${left}px`,
    width: `${dropW}px`,
    zIndex: 200,
    ...(goUp
      ? { bottom: `${window.innerHeight - rect.top + 4}px` }
      : { top: `${rect.bottom + 4}px` }),
  };
}

function togglePhoneDdi() {
  if (!phoneDdiOpen.value) {
    phoneDdiDropStyle.value = fixedDropStyle(phoneDdiTriggerRef.value, 176);
  }
  phoneDdiOpen.value = !phoneDdiOpen.value;
}

// ── seletor de tags do contato ────────────────────────────────────────────────
const tags = computed(() => contact.value.labels ?? []);
const showTagMenu = ref(false);
const tagSearch = ref('');
const tagHighlight = ref(0);
const tagTriggerRef = ref(null);
const tagMenuStyle = ref({});
const tagCaptureRef = ref(null);

const filteredAccountLabels = computed(() => {
  const q = tagSearch.value.toLowerCase();
  if (!q) return accountLabels.value ?? [];
  return (accountLabels.value ?? [])
    .filter(l => l.title.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.title.toLowerCase().indexOf(q) - b.title.toLowerCase().indexOf(q)
    );
});

watch(filteredAccountLabels, () => {
  tagHighlight.value = 0;
});
watch(tagSearch, () => {
  tagHighlight.value = 0;
});

function openTagMenu(e) {
  e.stopPropagation();
  if (!showTagMenu.value) {
    tagMenuStyle.value = fixedDropStyle(tagTriggerRef.value, 176);
  }
  showTagMenu.value = !showTagMenu.value;
  tagSearch.value = '';
  tagHighlight.value = 0;
  if (showTagMenu.value) nextTick(() => tagCaptureRef.value?.focus());
}

function isTagActive(title) {
  return tags.value.includes(title);
}

async function updateTags(updated) {
  const { data } = await ContactAPI.updateContactLabels(
    contact.value.id,
    updated
  );
  store.commit('kanban/UPDATE_CARD', {
    ...liveCard.value,
    contact: { ...contact.value, labels: data.payload ?? updated },
  });
}

async function toggleTag(title) {
  const existing = tags.value;
  const updated = existing.includes(title)
    ? existing.filter(l => l !== title)
    : [...existing, title];
  await updateTags(updated);
}

async function removeTag(title, e) {
  e.stopPropagation();
  await updateTags(tags.value.filter(l => l !== title));
}

function tagColor(label) {
  const found = (accountLabels.value ?? []).find(
    l => l.title?.toLowerCase() === label.toLowerCase()
  );
  if (found?.color) return { bg: `${found.color}22`, text: found.color };
  return { bg: 'rgba(99,102,241,0.12)', text: '#4338ca' };
}

function onTagInput(e) {
  tagSearch.value = e.target.value;
}

function onTagKeydown(e) {
  if (!showTagMenu.value) return;
  e.stopImmediatePropagation();
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    tagHighlight.value = Math.min(
      tagHighlight.value + 1,
      filteredAccountLabels.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    tagHighlight.value = Math.max(tagHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const lbl = filteredAccountLabels.value[tagHighlight.value];
    if (lbl) toggleTag(lbl.title);
  } else if (e.key === 'Escape') {
    showTagMenu.value = false;
    tagSearch.value = '';
  }
}

watch(showTagMenu, val => {
  if (val) window.addEventListener('keydown', onTagKeydown);
  else {
    window.removeEventListener('keydown', onTagKeydown);
    tagSearch.value = '';
    if (tagCaptureRef.value) tagCaptureRef.value.value = '';
  }
});

const columns = computed(() =>
  props.columns.length ? props.columns : (props.card.columns ?? [])
);

const columnOptions = computed(() =>
  columns.value.map(c => ({ value: c.id, label: c.name }))
);

const { getLabels, saveOptions } = useNativeFieldOptions();
const SOURCE_TAGS = getLabels('source');
const sourceOptions = computed(() => {
  const fromContact = (contact.value.labels ?? []).filter(l =>
    SOURCE_TAGS.includes(l.toLowerCase())
  );
  const unique = [...new Set([...SOURCE_TAGS, ...fromContact])];
  return unique;
});

const PRODUCT_TAGS = getLabels('product');
const productOptions = computed(() => {
  const unique = [...new Set([...PRODUCT_TAGS, ...props.products])];
  return unique;
});

const CITY_TAGS = getLabels('city');
const cityOptions = computed(() => {
  const unique = [
    ...new Set([...CITY_TAGS, ...(city.value ? [city.value] : [])]),
  ];
  return unique;
});

// ── datepicker de previsão ────────────────────────────────────────────────────
const showForecastPicker = ref(false);
const forecastPickerStyle = ref({});
const forecastTriggerRef = ref(null);

function openForecastPicker() {
  const el = forecastTriggerRef.value;
  if (el) {
    const rect = el.getBoundingClientRect();
    const pickerHeight = 390;
    const goUp = rect.bottom + pickerHeight > window.innerHeight;
    forecastPickerStyle.value = {
      position: 'fixed',
      left: `${rect.left}px`,
      zIndex: 200,
      ...(goUp
        ? { bottom: `${window.innerHeight - rect.top + 4}px` }
        : { top: `${rect.bottom + 4}px` }),
    };
  }
  showForecastPicker.value = true;
}

function formatForecastLabel(date, time) {
  if (!date) return null;
  const d = new Date(date + 'T00:00:00').toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  return time ? `${d} às ${time}` : d;
}

// ── edição inline de valor ────────────────────────────────────────────────────
const editingValue = ref(false);

// ── campo valor com máscara de milhar ────────────────────────────────────────
// valueDigits: string com todos os dígitos (inteiros + centavos), ex: "5000000097"
// Os últimos 2 dígitos são sempre centavos.
// Ao digitar/apagar, identifica a posição do cursor no texto formatado,
// converte para posição dentro de valueDigits, opera lá, reformata e
// reposiciona o cursor no texto novo na posição equivalente.

const valueDigits = ref('');
const valueInputRef = ref(null);

function digitsFromRaw(raw) {
  if (raw === '' || raw == null) return '';
  const num = parseFloat(String(raw).replace(',', '.'));
  if (Number.isNaN(num)) return '';
  return String(Math.round(num * 100));
}

function formatFromDigits(digits) {
  if (!digits) return '';
  const padded = digits.padStart(3, '0');
  const intPart = padded.slice(0, -2);
  const decPart = padded.slice(-2);
  const intFormatted = new Intl.NumberFormat('pt-BR').format(Number(intPart));
  return `${intFormatted},${decPart}`;
}

// Conta quantos dígitos existem nos primeiros `cursorPos` chars do texto formatado
function digitsBeforeCursor(formatted, cursorPos) {
  return formatted.slice(0, cursorPos).replace(/\D/g, '').length;
}

// Encontra a posição no texto formatado após N dígitos (contando da esquerda)
function cursorAfterNDigits(formatted, n) {
  let count = 0;
  for (let i = 0; i < formatted.length; i += 1) {
    if (/\d/.test(formatted[i])) count += 1;
    if (count === n) return i + 1;
  }
  return formatted.length;
}

const displayValue = ref('');

watch(
  () => props.value,
  v => {
    const d = digitsFromRaw(v);
    valueDigits.value = d;
    displayValue.value = d ? formatFromDigits(d) : '';
  },
  { immediate: true }
);

function onValueKeydown(e) {
  const isDigit = e.key >= '0' && e.key <= '9';
  const isBackspace = e.key === 'Backspace';
  const isDelete = e.key === 'Delete';
  const isAllowed =
    ['ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(e.key) ||
    e.ctrlKey ||
    e.metaKey;

  if (e.key === 'Enter' || e.key === 'Escape') {
    editingValue.value = false;
    return;
  }
  if (isAllowed) return;
  if (!isDigit && !isBackspace && !isDelete) {
    e.preventDefault();
    return;
  }

  e.preventDefault();

  const input = valueInputRef.value;
  const cursorPos = input ? input.selectionStart : valueDigits.value.length;
  const formatted = displayValue.value;
  const digitsBefore = digitsBeforeCursor(formatted, cursorPos);

  let d = valueDigits.value;

  if (isDigit) {
    // insere o dígito na posição correta dentro de d
    d = d.slice(0, digitsBefore) + e.key + d.slice(digitsBefore);
  } else if (isBackspace) {
    if (digitsBefore === 0) return;
    d = d.slice(0, digitsBefore - 1) + d.slice(digitsBefore);
  } else if (isDelete) {
    if (digitsBefore >= d.length) return;
    d = d.slice(0, digitsBefore) + d.slice(digitsBefore + 1);
  }

  valueDigits.value = d;
  const newFormatted = d ? formatFromDigits(d) : '';
  displayValue.value = newFormatted;
  const numeric = d ? (Number(d) / 100).toFixed(2) : '';
  emit('update:value', numeric);

  // reposicionar cursor: após inserção fica após o dígito inserido;
  // após deleção fica na mesma posição de dígitos
  nextTick(() => {
    if (!valueInputRef.value || !newFormatted) return;
    let targetDigitIndex;
    if (isDigit) {
      targetDigitIndex = digitsBefore + 1;
    } else {
      targetDigitIndex = isBackspace ? digitsBefore - 1 : digitsBefore;
    }
    const newCursor = cursorAfterNDigits(newFormatted, targetDigitIndex);
    valueInputRef.value.setSelectionRange(newCursor, newCursor);
  });
}

function onValueBlur() {
  const d = valueDigits.value;
  displayValue.value = d ? formatFromDigits(d) : '';
  editingValue.value = false;
}

const labelClass =
  'text-[11px] font-medium text-n-slate-10 uppercase tracking-wide';

// ── cidade ───────────────────────────────────────────────────────────────────
const citySearch = ref('');
const cityHighlight = ref(0);
const showCityDropdown = ref(false);
const cityTriggerRef = ref(null);
const cityDropStyle = ref({});
const cityCaptureRef = ref(null);

const filteredCityOptions = computed(() => {
  const q = citySearch.value.toLowerCase().trim();
  if (!q) return cityOptions.value;
  return cityOptions.value
    .filter(o => o.toLowerCase().includes(q))
    .sort((a, b) => a.toLowerCase().indexOf(q) - b.toLowerCase().indexOf(q));
});

const canCreateCity = computed(() => {
  const q = citySearch.value.trim();
  if (!q) return false;
  return !cityOptions.value.some(o => o.toLowerCase() === q.toLowerCase());
});

function openCityDropdown() {
  citySearch.value = '';
  cityHighlight.value = 0;
  cityDropStyle.value = fixedDropStyle(cityTriggerRef.value);
  showCityDropdown.value = true;
  nextTick(() => cityCaptureRef.value?.focus());
}

function selectCity(val) {
  setCity(val);
  showCityDropdown.value = false;
  citySearch.value = '';
}

function createAndSelectCity() {
  const newCity = citySearch.value.trim();
  if (!newCity) return;
  CITY_TAGS.push(newCity);
  saveOptions(
    'city',
    CITY_TAGS.map(label => ({ key: toSlug(label), label }))
  );
  selectCity(newCity);
}

function onCityInput(e) {
  citySearch.value = e.target.value;
  cityHighlight.value = 0;
}

function onCityKeydown(e) {
  if (!showCityDropdown.value) return;
  const items = canCreateCity.value
    ? [...filteredCityOptions.value, '__create__']
    : filteredCityOptions.value;
  if (e.key === 'Escape') {
    showCityDropdown.value = false;
    citySearch.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    cityHighlight.value = Math.min(cityHighlight.value + 1, items.length - 1);
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    cityHighlight.value = Math.max(cityHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = items[cityHighlight.value];
    if (item === '__create__') createAndSelectCity();
    else if (item) selectCity(item);
  }
}

watch(showCityDropdown, val => {
  if (val) window.addEventListener('keydown', onCityKeydown);
  else {
    window.removeEventListener('keydown', onCityKeydown);
    citySearch.value = '';
    if (cityCaptureRef.value) cityCaptureRef.value.value = '';
  }
});

// ── fonte ────────────────────────────────────────────────────────────────────
const sourceSearch = ref('');
const sourceHighlight = ref(0);
const showSourceDropdown = ref(false);
const sourceTriggerRef = ref(null);
const sourceDropStyle = ref({});
const sourceCaptureRef = ref(null);

const filteredSourceOptions = computed(() => {
  const q = sourceSearch.value.toLowerCase().trim();
  if (!q) return sourceOptions.value;
  return sourceOptions.value
    .filter(o => o.toLowerCase().includes(q))
    .sort((a, b) => a.toLowerCase().indexOf(q) - b.toLowerCase().indexOf(q));
});

const canCreateSource = computed(() => {
  const q = sourceSearch.value.trim();
  if (!q) return false;
  return !sourceOptions.value.some(o => o.toLowerCase() === q.toLowerCase());
});

// com utm_source reconhecida, o backend deriva a Origem da UTM e ignora edição
// manual: o campo fica travado para não parecer que a troca foi aceita
const sourceLocked = computed(() => Boolean(props.card?.source_from_utm));

function openSourceDropdown() {
  if (sourceLocked.value) return;
  sourceSearch.value = '';
  sourceHighlight.value = 0;
  sourceDropStyle.value = fixedDropStyle(sourceTriggerRef.value);
  showSourceDropdown.value = true;
  nextTick(() => sourceCaptureRef.value?.focus());
}

function selectSource(val) {
  emit('update:source', val);
  showSourceDropdown.value = false;
  sourceSearch.value = '';
}

function createAndSelectSource() {
  const newSource = sourceSearch.value.trim();
  if (!newSource) return;
  SOURCE_TAGS.push(newSource);
  saveOptions(
    'source',
    SOURCE_TAGS.map(label => ({ key: toSlug(label), label }))
  );
  selectSource(newSource);
}

function onSourceInput(e) {
  sourceSearch.value = e.target.value;
  sourceHighlight.value = 0;
}

function onSourceKeydown(e) {
  if (!showSourceDropdown.value) return;
  const items = canCreateSource.value
    ? [...filteredSourceOptions.value, '__create__']
    : filteredSourceOptions.value;
  if (e.key === 'Escape') {
    showSourceDropdown.value = false;
    sourceSearch.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    sourceHighlight.value = Math.min(
      sourceHighlight.value + 1,
      items.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    sourceHighlight.value = Math.max(sourceHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = items[sourceHighlight.value];
    if (item === '__create__') createAndSelectSource();
    else if (item) selectSource(item);
  }
}

watch(showSourceDropdown, val => {
  if (val) window.addEventListener('keydown', onSourceKeydown);
  else {
    window.removeEventListener('keydown', onSourceKeydown);
    sourceSearch.value = '';
    if (sourceCaptureRef.value) sourceCaptureRef.value.value = '';
  }
});

// ── produto ──────────────────────────────────────────────────────────────────
const showProductDropdown = ref(false);
const productSearch = ref('');
const productHighlight = ref(0);
const productTriggerRef = ref(null);
const productDropStyle = ref({});
const productCaptureRef = ref(null);

const filteredProductOptions = computed(() => {
  const q = productSearch.value.toLowerCase().trim();
  if (!q) return productOptions.value;
  return productOptions.value
    .filter(o => o.toLowerCase().includes(q))
    .sort((a, b) => a.toLowerCase().indexOf(q) - b.toLowerCase().indexOf(q));
});

const canCreateProduct = computed(() => {
  const q = productSearch.value.trim();
  if (!q) return false;
  return !productOptions.value.some(o => o.toLowerCase() === q.toLowerCase());
});

const productFitLabel = useMultiSelectLabel(
  productTriggerRef,
  computed(() => props.products),
  34
);

const productChipLabel = computed(() => productFitLabel.value || null);

function openProductDropdown() {
  productSearch.value = '';
  productHighlight.value = 0;
  productDropStyle.value = fixedDropStyle(productTriggerRef.value);
  showProductDropdown.value = true;
  nextTick(() => productCaptureRef.value?.focus());
}

function toggleProduct(name) {
  const updated = props.products.includes(name)
    ? props.products.filter(p => p !== name)
    : [...props.products, name];
  emit('update:products', updated);
}

function createAndToggleProduct() {
  const newProduct = productSearch.value.trim();
  if (!newProduct) return;
  PRODUCT_TAGS.push(newProduct);
  saveOptions(
    'product',
    PRODUCT_TAGS.map(label => ({ key: toSlug(label), label }))
  );
  toggleProduct(newProduct);
  productSearch.value = '';
  productHighlight.value = 0;
}

function onProductInput(e) {
  productSearch.value = e.target.value;
  productHighlight.value = 0;
}

function onProductKeydown(e) {
  if (!showProductDropdown.value) return;
  const items = canCreateProduct.value
    ? [...filteredProductOptions.value, '__create__']
    : filteredProductOptions.value;
  if (e.key === 'Escape') {
    showProductDropdown.value = false;
    productSearch.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    productHighlight.value = Math.min(
      productHighlight.value + 1,
      items.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    productHighlight.value = Math.max(productHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = items[productHighlight.value];
    if (item === '__create__') createAndToggleProduct();
    else if (item) toggleProduct(item);
  }
}

watch(showProductDropdown, val => {
  if (val) window.addEventListener('keydown', onProductKeydown);
  else {
    window.removeEventListener('keydown', onProductKeydown);
    productSearch.value = '';
    if (productCaptureRef.value) productCaptureRef.value.value = '';
  }
});

watch(filteredProductOptions, () => {
  productHighlight.value = 0;
});

// ── pipeline ──────────────────────────────────────────────────────────────────
const showPipelineDropdown = ref(false);
const pipelineSearch = ref('');
const pipelineHighlight = ref(0);
const pipelineTriggerRef = ref(null);
const pipelineDropStyle = ref({});
const pipelineCaptureRef = ref(null);

const filteredPipelines = computed(() => {
  const q = pipelineSearch.value.toLowerCase();
  if (!q) return props.pipelines;
  return props.pipelines.filter(p => p.name.toLowerCase().includes(q));
});

const currentPipeline = computed(
  () => props.pipelines.find(p => p.id === props.pipelineId) ?? null
);

function openPipelineDropdown() {
  pipelineSearch.value = '';
  pipelineHighlight.value = 0;
  pipelineDropStyle.value = fixedDropStyle(pipelineTriggerRef.value);
  showPipelineDropdown.value = true;
  nextTick(() => pipelineCaptureRef.value?.focus());
}

function selectPipeline(id) {
  emit('update:pipeline-id', id);
  showPipelineDropdown.value = false;
  pipelineSearch.value = '';
}

function onPipelineInput(e) {
  pipelineSearch.value = e.target.value;
  pipelineHighlight.value = 0;
}

function onPipelineKeydown(e) {
  if (!showPipelineDropdown.value) return;
  const items = filteredPipelines.value;
  if (e.key === 'Escape') {
    showPipelineDropdown.value = false;
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    pipelineHighlight.value = Math.min(
      pipelineHighlight.value + 1,
      items.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    pipelineHighlight.value = Math.max(pipelineHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = items[pipelineHighlight.value];
    if (item) selectPipeline(item.id);
  }
}

watch(showPipelineDropdown, val => {
  if (val) window.addEventListener('keydown', onPipelineKeydown);
  else {
    window.removeEventListener('keydown', onPipelineKeydown);
    pipelineSearch.value = '';
    if (pipelineCaptureRef.value) pipelineCaptureRef.value.value = '';
  }
});

// ── etapa ─────────────────────────────────────────────────────────────────────
const showStageDropdown = ref(false);
const stageSearch = ref('');
const stageHighlight = ref(0);
const stageTriggerRef = ref(null);
const stageDropStyle = ref({});
const stageCaptureRef = ref(null);

const filteredStages = computed(() => {
  const q = stageSearch.value.toLowerCase();
  if (!q) return columnOptions.value;
  return columnOptions.value.filter(c => c.label.toLowerCase().includes(q));
});

const currentStage = computed(
  () => columnOptions.value.find(c => c.value === props.columnId) ?? null
);

function openStageDropdown() {
  stageSearch.value = '';
  stageHighlight.value = 0;
  stageDropStyle.value = fixedDropStyle(stageTriggerRef.value);
  showStageDropdown.value = true;
  nextTick(() => stageCaptureRef.value?.focus());
}

function selectStage(id) {
  emit('update:column-id', id);
  showStageDropdown.value = false;
  stageSearch.value = '';
}

function onStageInput(e) {
  stageSearch.value = e.target.value;
  stageHighlight.value = 0;
}

function onStageKeydown(e) {
  if (!showStageDropdown.value) return;
  const items = filteredStages.value;
  if (e.key === 'Escape') {
    showStageDropdown.value = false;
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    stageHighlight.value = Math.min(stageHighlight.value + 1, items.length - 1);
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    stageHighlight.value = Math.max(stageHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = items[stageHighlight.value];
    if (item) selectStage(item.value);
  }
}

watch(showStageDropdown, val => {
  if (val) window.addEventListener('keydown', onStageKeydown);
  else {
    window.removeEventListener('keydown', onStageKeydown);
    stageSearch.value = '';
    if (stageCaptureRef.value) stageCaptureRef.value.value = '';
  }
});

// ── edição inline de contato ──────────────────────────────────────────────────
const editingContact = ref(false);
const contactSearch = ref('');
const contactResults = ref([]);
const showContactDropdown = ref(false);
const contactInputRef = ref(null);
const contactDropStyle = ref({});
const isSelectingContact = ref(false);
const contactHighlightedIndex = ref(0);

const contactDisplayName = computed(
  () => contact.value.company_name || contact.value.name || ''
);

function normalizeContact(c) {
  return {
    ...c,
    company_name:
      c.company_name ?? c.additional_attributes?.company_name ?? null,
  };
}

let contactSearchDebounce = null;
let contactSearchRequestId = 0;

async function performContactSearch(query) {
  contactSearchRequestId += 1;
  const requestId = contactSearchRequestId;
  try {
    const { data } = await ContactAPI.search(query, 1, 'name');
    if (requestId !== contactSearchRequestId) return; // resposta obsoleta
    contactResults.value = (data?.payload || []).map(normalizeContact);
    contactHighlightedIndex.value = 0;
    showContactDropdown.value = contactResults.value.length > 0;
    if (showContactDropdown.value) {
      contactDropStyle.value = fixedDropStyle(contactInputRef.value, 200);
    }
  } catch {
    if (requestId === contactSearchRequestId) contactResults.value = [];
  }
}

// debounce evita disparar uma requisição a cada tecla digitada; o
// requestId acima também descarta respostas que cheguem fora de ordem.
function searchContacts(query) {
  clearTimeout(contactSearchDebounce);
  if (!query || query.length < 1) {
    contactSearchRequestId += 1;
    contactResults.value = [];
    showContactDropdown.value = false;
    return;
  }
  contactSearchDebounce = setTimeout(() => performContactSearch(query), 300);
}

function cancelContactEdit() {
  if (isSelectingContact.value) return;
  editingContact.value = false;
  contactSearch.value = '';
  contactResults.value = [];
  showContactDropdown.value = false;
}

function onContactKeydown(e) {
  if (!showContactDropdown.value) return;
  if (e.key === 'Escape') {
    cancelContactEdit();
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    contactHighlightedIndex.value = Math.min(
      contactHighlightedIndex.value + 1,
      contactResults.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    contactHighlightedIndex.value = Math.max(
      contactHighlightedIndex.value - 1,
      0
    );
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = contactResults.value[contactHighlightedIndex.value];
    // eslint-disable-next-line no-use-before-define
    if (item) selectContact(item);
  }
}

watch(showContactDropdown, val => {
  if (val) window.addEventListener('keydown', onContactKeydown);
  else window.removeEventListener('keydown', onContactKeydown);
});

function openContactEdit() {
  contactSearch.value = contactDisplayName.value;
  editingContact.value = true;
  nextTick(() => {
    contactInputRef.value?.focus();
    contactInputRef.value?.select();
  });
}

const agentOptions = computed(() =>
  props.agents.map(a => ({
    id: a.id,
    label: a.name,
    avatar: a.thumbnail ?? null,
    color: a.color,
  }))
);

// ── responsável com busca e teclado ──────────────────────────────────────────
const assigneeOpen = ref(false);
const assigneeSearch = ref('');
const assigneeHighlight = ref(0);
const assigneeTriggerRef = ref(null);
const assigneeDropStyle = ref({});
const assigneeCaptureRef = ref(null);

const assigneeSelected = computed(
  () => agentOptions.value.find(a => a.id === props.assigneeId) ?? null
);

const filteredAgents = computed(() => {
  const q = assigneeSearch.value.toLowerCase().trim();
  if (!q) return agentOptions.value;
  return agentOptions.value.filter(a => a.label.toLowerCase().includes(q));
});

const assigneeNavigable = computed(() => {
  const items = filteredAgents.value.map(a => ({ id: a.id }));
  items.push({ id: 'none' });
  return items;
});

function openAssignee() {
  assigneeSearch.value = '';
  assigneeHighlight.value = 0;
  const el = assigneeTriggerRef.value;
  if (el) {
    const rect = el.getBoundingClientRect();
    const dropW = 208;
    const left = Math.min(
      Math.max(rect.left, 8),
      window.innerWidth - dropW - 8
    );
    assigneeDropStyle.value = {
      position: 'fixed',
      bottom: `${window.innerHeight - rect.top + 6}px`,
      left: `${left}px`,
      width: `${dropW}px`,
    };
  }
  assigneeOpen.value = true;
  nextTick(() => assigneeCaptureRef.value?.focus());
}

function selectAssignee(id) {
  const newId = id === 'none' ? null : id;
  emit('update:assignee-id', newId);
  if (newId !== null && props.watcherIds.includes(newId)) {
    emit(
      'update:watcher-ids',
      props.watcherIds.filter(w => w !== newId)
    );
  }
  assigneeOpen.value = false;
  assigneeSearch.value = '';
}

function onAssigneeInput(e) {
  assigneeSearch.value = e.target.value;
  assigneeHighlight.value = 0;
}

function onAssigneeKeydown(e) {
  if (e.key === 'Escape') {
    assigneeOpen.value = false;
    assigneeSearch.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    assigneeHighlight.value = Math.min(
      assigneeHighlight.value + 1,
      assigneeNavigable.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    assigneeHighlight.value = Math.max(assigneeHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = assigneeNavigable.value[assigneeHighlight.value];
    if (item) selectAssignee(item.id);
  }
}

watch(assigneeOpen, val => {
  if (val) window.addEventListener('keydown', onAssigneeKeydown);
  else {
    window.removeEventListener('keydown', onAssigneeKeydown);
    assigneeSearch.value = '';
    if (assigneeCaptureRef.value) assigneeCaptureRef.value.value = '';
  }
});

watch(filteredAgents, () => {
  assigneeHighlight.value = 0;
});

async function selectContact(c) {
  isSelectingContact.value = false;
  showContactDropdown.value = false;
  editingContact.value = false;
  if (props.isNew) {
    emit('contactSelected', c);
    return;
  }
  await store.dispatch('kanban/updateCard', {
    cardId: props.card.id,
    data: { contact_id: c.id },
  });
  emit('contactUpdated');
}

function focusContactSearch() {
  if (!editingContact.value) {
    openContactEdit();
    nextTick(() => {
      contactInputRef.value?.focus();
    });
  } else {
    contactInputRef.value?.focus();
  }
}

defineExpose({ focusContactSearch, forecastTriggerRef });

// ── seguidores ────────────────────────────────────────────────────────────────
const watchersOpen = ref(false);
const watchersSearch = ref('');
const watchersHighlight = ref(0);
const watchersTriggerRef = ref(null);
const watchersDropStyle = ref({});
const watchersCaptureRef = ref(null);

const watcherAgents = computed(() =>
  agentOptions.value.filter(a => props.watcherIds.includes(a.id))
);

const filteredWatcherAgents = computed(() => {
  const q = watchersSearch.value.toLowerCase().trim();
  if (!q) return agentOptions.value;
  return agentOptions.value.filter(a => a.label.toLowerCase().includes(q));
});

const watchersNavigable = computed(() => [
  ...filteredWatcherAgents.value.map(a => ({ id: a.id })),
  { id: 'clear' },
]);

function openWatchers() {
  watchersSearch.value = '';
  watchersHighlight.value = 0;
  const el = watchersTriggerRef.value;
  if (el) {
    const rect = el.getBoundingClientRect();
    const dropW = 208;
    const left = Math.min(
      Math.max(rect.left, 8),
      window.innerWidth - dropW - 8
    );
    watchersDropStyle.value = {
      position: 'fixed',
      bottom: `${window.innerHeight - rect.top + 6}px`,
      left: `${left}px`,
      width: `${dropW}px`,
      zIndex: 200,
    };
  }
  watchersOpen.value = true;
  nextTick(() => watchersCaptureRef.value?.focus());
}

const watchersMoveConfirmId = ref(null);

function toggleWatcher(id) {
  if (id === props.assigneeId) {
    watchersMoveConfirmId.value = id;
    return;
  }
  const current = props.watcherIds;
  const updated = current.includes(id)
    ? current.filter(w => w !== id)
    : [...current, id];
  emit('update:watcher-ids', updated);
}

function confirmMoveAssigneeToWatcher() {
  const id = watchersMoveConfirmId.value;
  watchersMoveConfirmId.value = null;
  emit('update:assignee-id', null);
  const updated = props.watcherIds.includes(id)
    ? props.watcherIds
    : [...props.watcherIds, id];
  emit('update:watcher-ids', updated);
  watchersOpen.value = false;
}

function onWatchersInput(e) {
  watchersSearch.value = e.target.value;
  watchersHighlight.value = 0;
}

function onWatchersKeydown(e) {
  if (e.key === 'Escape') {
    watchersOpen.value = false;
    watchersSearch.value = '';
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    watchersHighlight.value = Math.min(
      watchersHighlight.value + 1,
      watchersNavigable.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    watchersHighlight.value = Math.max(watchersHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = watchersNavigable.value[watchersHighlight.value];
    if (item?.id === 'clear') {
      emit('update:watcher-ids', []);
      watchersOpen.value = false;
    } else if (item) {
      toggleWatcher(item.id);
    }
  }
}

watch(watchersOpen, val => {
  if (val) window.addEventListener('keydown', onWatchersKeydown);
  else {
    window.removeEventListener('keydown', onWatchersKeydown);
    watchersSearch.value = '';
    if (watchersCaptureRef.value) watchersCaptureRef.value.value = '';
  }
});

watch(filteredWatcherAgents, () => {
  watchersHighlight.value = 0;
});

onUnmounted(() => {
  window.removeEventListener('keydown', onCityKeydown);
  window.removeEventListener('keydown', onSourceKeydown);
  window.removeEventListener('keydown', onPipelineKeydown);
  window.removeEventListener('keydown', onStageKeydown);
  window.removeEventListener('keydown', onTagKeydown);
  window.removeEventListener('keydown', onAssigneeKeydown);
  window.removeEventListener('keydown', onWatchersKeydown);
  window.removeEventListener('keydown', onProductKeydown);
  clearTimeout(contactSearchDebounce);
});
</script>

<template>
  <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
    <!-- 1. Contato | Empresa -->
    <div class="flex flex-col gap-0.5">
      <span :class="labelClass">
        {{ t('KANBAN.OPPORTUNITY_MODAL.FIELD_CONTACT') }}
      </span>
      <div
        class="relative flex items-center gap-1 w-full group/contact"
        @click.stop
      >
        <div
          v-if="editingContact"
          class="flex items-center gap-2 flex-1 min-w-0 bg-n-alpha-1 rounded px-2 py-1 min-h-[30px] border-b border-n-brand"
        >
          <input
            ref="contactInputRef"
            v-model="contactSearch"
            class="flex-1 text-sm text-n-slate-12 !bg-transparent !outline-none !shadow-none !border-none ring-0 focus:ring-0 min-w-0 appearance-none"
            :placeholder="
              t('KANBAN.OPPORTUNITY_MODAL.CONTACT_SEARCH_PLACEHOLDER')
            "
            @input="searchContacts($event.target.value)"
            @blur="cancelContactEdit"
          />
        </div>
        <button
          v-else
          class="flex items-center gap-2 flex-1 min-w-0 text-left bg-n-alpha-1 hover:bg-n-alpha-2 rounded px-2 py-1 min-h-[30px] transition-colors"
          @click="openContactEdit()"
          @contextmenu.prevent="openContactEdit"
        >
          <span class="text-sm text-n-slate-12 truncate flex-1 leading-none">
            {{ contactDisplayName || '—' }}
          </span>
          <span
            class="i-lucide-pencil size-3 text-n-slate-9 opacity-0 group-hover/contact:opacity-60 transition-opacity flex-shrink-0"
          />
        </button>
        <span
          v-if="!editingContact && contact.id"
          class="flex-shrink-0 text-n-slate-9 hover:text-n-brand cursor-pointer opacity-0 group-hover/contact:opacity-100 max-w-0 group-hover/contact:max-w-[20px] overflow-hidden transition-all duration-200"
          :title="t('KANBAN.OPPORTUNITY_MODAL.OPEN_CONTACT')"
          @click.stop="emit('openContact')"
        >
          <span class="i-lucide-arrow-up-right size-3.5" />
        </span>
        <div
          v-if="showContactDropdown && contactResults.length"
          class="bg-n-background border border-n-weak rounded-xl shadow-xl py-1 max-h-44 overflow-y-auto min-w-[200px]"
          :style="contactDropStyle"
          @mousedown="isSelectingContact = true"
        >
          <button
            v-for="(c, idx) in contactResults"
            :key="c.id"
            type="button"
            class="w-full text-left flex items-center gap-2 px-3 py-2 text-sm transition-colors"
            :class="
              idx === contactHighlightedIndex
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : 'text-n-slate-12 hover:bg-n-alpha-1'
            "
            @click="selectContact(c)"
          >
            <div
              class="size-6 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 text-[9px] font-bold text-white bg-n-slate-5"
              :style="
                contactAvatar(c)
                  ? {
                      backgroundImage: `url(${contactAvatar(c)})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }
                  : {}
              "
            >
              <span v-if="!contactAvatar(c)">{{
                initials(c.company_name || c.name)
              }}</span>
            </div>
            <div class="min-w-0">
              <p class="truncate">{{ c.name }}</p>
              <p v-if="c.company_name" class="text-n-slate-9 text-xs truncate">
                {{ c.company_name }}
              </p>
            </div>
          </button>
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-0.5">
      <span :class="labelClass">
        {{ t('KANBAN.OPPORTUNITY_MODAL.FIELD_COMPANY') }}
      </span>
      <div
        class="relative flex items-center group/company bg-n-alpha-1 hover:bg-n-alpha-2 rounded px-2 py-1 min-h-[30px] transition-colors"
        :class="editingCompany ? 'border-b border-n-brand' : ''"
        @click.stop
      >
        <input
          v-if="editingCompany"
          ref="companyInputRef"
          v-model="companyDraft"
          class="flex-1 text-sm text-n-slate-12 !bg-transparent !outline-none !shadow-none !border-none ring-0 focus:ring-0 w-full appearance-none"
          :placeholder="t('KANBAN.OPPORTUNITY_MODAL.NO_COMPANY')"
          @blur="commitCompany"
          @keydown="onCompanyKeydown"
        />
        <button
          v-else
          class="flex items-center gap-1.5 w-full text-left p-0"
          @click="startEditCompany"
        >
          <span
            class="text-sm truncate flex-1"
            :class="companyName ? 'text-n-slate-12' : 'text-n-slate-9'"
          >
            {{ companyName || t('KANBAN.OPPORTUNITY_MODAL.NO_COMPANY') }}
          </span>
          <span
            class="i-lucide-pencil size-3 text-n-slate-9 opacity-0 group-hover/company:opacity-60 transition-opacity flex-shrink-0"
          />
        </button>
      </div>
    </div>

    <!-- 2. Telefone | E-mail -->
    <div class="flex flex-col gap-0.5">
      <span :class="labelClass">
        {{ t('KANBAN.OPPORTUNITY_MODAL.FIELD_PHONE') }}
      </span>
      <div class="flex items-center gap-1 w-full group/phone" @click.stop>
        <!-- Modo edição -->
        <div
          v-if="editingPhone"
          ref="phoneEditContainerRef"
          class="flex items-center gap-1 flex-1 min-w-0 bg-n-alpha-1 rounded px-2 py-1 min-h-[30px] border-b border-n-brand"
          @focusin="onPhoneFocusIn"
          @focusout="onPhoneFocusOut"
        >
          <div class="relative flex-shrink-0">
            <button
              ref="phoneDdiTriggerRef"
              type="button"
              class="flex items-center gap-0.5 text-xs leading-none text-n-slate-11 hover:text-n-slate-12 focus:outline-none"
              @click="togglePhoneDdi"
            >
              <span class="leading-none">{{
                DDI_LIST.find(d => d.code === phoneDdi)?.flag
              }}</span>
              <span class="leading-none">{{ phoneDdi }}</span>
              <span class="i-lucide-chevron-down size-2.5 flex-shrink-0" />
            </button>
            <div
              v-if="phoneDdiOpen"
              class="bg-n-background border border-n-weak rounded-xl shadow-xl py-1"
              :style="phoneDdiDropStyle"
            >
              <button
                v-for="ddi in DDI_LIST"
                :key="ddi.code"
                type="button"
                class="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-n-alpha-1 transition-colors text-n-slate-12"
                :class="ddi.code === phoneDdi ? 'text-n-brand font-medium' : ''"
                @click="
                  phoneDdi = ddi.code;
                  phoneDdiOpen = false;
                  nextTick(() => phoneLocalRef?.focus());
                "
              >
                <span>{{ ddi.flag }}</span>
                <span class="flex-1 text-left">{{ ddi.label }}</span>
                <span class="text-n-slate-10">{{ ddi.code }}</span>
              </button>
            </div>
            <div
              v-if="phoneDdiOpen"
              class="fixed inset-0 z-[199]"
              @click="phoneDdiOpen = false"
            />
          </div>
          <div
            ref="phoneLocalRef"
            contenteditable="true"
            inputmode="tel"
            class="flex-1 text-sm text-n-slate-12 outline-none"
            @keydown="onPhoneKeydown"
            @input="e => maskPhoneInput(e.target)"
          />
        </div>
        <!-- Modo leitura -->
        <button
          v-else
          class="flex items-center gap-1.5 flex-1 min-w-0 text-left bg-n-alpha-1 hover:bg-n-alpha-2 rounded px-2 py-1 min-h-[30px] transition-colors overflow-hidden"
          @click="startEditPhone"
        >
          <span v-if="phone" class="flex-shrink-0 text-sm">{{
            DDI_LIST.find(d => phone.startsWith(d.code + ' '))?.flag ?? '🇧🇷'
          }}</span>
          <span
            class="text-sm truncate flex-1"
            :class="phone ? 'text-n-slate-12' : 'text-n-slate-9'"
          >
            {{ formatPhoneLocal(extractPhoneDdiAndLocal(phone).local) || '—' }}
          </span>
          <span
            class="i-lucide-pencil size-3 text-n-slate-9 opacity-0 group-hover/phone:opacity-60 transition-opacity flex-shrink-0"
          />
        </button>
        <a
          v-if="!editingPhone && phone"
          :href="`https://wa.me/${phone.replace(/\D/g, '').startsWith('55') ? phone.replace(/\D/g, '') : '55' + phone.replace(/\D/g, '')}`"
          target="_blank"
          rel="noopener noreferrer"
          class="flex-shrink-0 text-n-slate-9 hover:text-n-teal-10 opacity-0 group-hover/phone:opacity-100 max-w-0 group-hover/phone:max-w-[20px] overflow-hidden transition-all duration-200"
          :title="t('KANBAN.OPPORTUNITY_MODAL.OPEN_WHATSAPP')"
          @click.stop
        >
          <span class="i-lucide-arrow-up-right size-3.5" />
        </a>
      </div>
    </div>
    <div class="flex flex-col gap-0.5">
      <span :class="labelClass">
        {{ t('KANBAN.OPPORTUNITY_MODAL.FIELD_EMAIL') }}
      </span>
      <div
        class="relative group/email flex items-center gap-1.5 bg-n-alpha-1 hover:bg-n-alpha-2 rounded px-2 py-1 min-h-[30px] transition-colors"
        :class="editingEmail ? 'border-b border-n-brand' : ''"
        @click.stop
      >
        <input
          ref="emailInputRef"
          v-model="emailDraft"
          type="text"
          :readonly="!editingEmail"
          :placeholder="
            editingEmail ? t('KANBAN.OPPORTUNITY_MODAL.NO_EMAIL') : ''
          "
          class="flex-1 text-sm !bg-transparent !outline-none !shadow-none !p-0 !m-0 !h-auto !rounded-none !border-none ring-0 focus:ring-0 w-full appearance-none transition-colors"
          :class="
            editingEmail
              ? 'cursor-text text-n-slate-12'
              : email
                ? 'cursor-pointer text-n-slate-12'
                : 'cursor-pointer text-n-slate-9'
          "
          @blur="commitEmail"
          @keydown="onEmailKeydown"
          @click="
            if (!editingEmail) {
              emailDraft = email ?? '';
              editingEmail = true;
              emailCommitting = false;
              $nextTick(() => {
                emailInputRef?.focus();
                emailInputRef?.select();
              });
            }
          "
        />
        <span
          v-if="!editingEmail"
          class="i-lucide-pencil size-3 text-n-slate-9 opacity-0 group-hover/email:opacity-60 transition-opacity flex-shrink-0"
        />
      </div>
    </div>

    <!-- 3. Cidade | Fonte -->
    <div class="flex flex-col gap-0.5">
      <span :class="labelClass">
        {{ t('KANBAN.OPPORTUNITY_MODAL.FIELD_CITY') }}
      </span>
      <div class="relative" @click.stop>
        <input
          ref="cityCaptureRef"
          type="text"
          class="sr-only"
          tabindex="-1"
          @input="onCityInput"
        />
        <button
          ref="cityTriggerRef"
          class="flex items-center gap-1.5 text-sm w-full text-left outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 min-h-[30px] bg-n-alpha-1 hover:bg-n-alpha-2 rounded px-2 py-1 transition-colors"
          @click="
            showCityDropdown ? (showCityDropdown = false) : openCityDropdown()
          "
        >
          <span
            v-if="showCityDropdown && citySearch"
            class="text-n-slate-10 truncate flex-1 leading-none"
            >{{ citySearch }}</span
          >
          <span
            v-else
            class="truncate flex-1 leading-none"
            :class="city ? 'text-n-slate-12' : 'text-n-slate-9'"
          >
            {{ city || t('KANBAN.OPPORTUNITY_MODAL.NO_CITY') }}
          </span>
          <span
            class="i-lucide-chevron-down size-3 text-n-slate-9 flex-shrink-0"
          />
        </button>
        <div
          v-if="showCityDropdown"
          class="bg-n-background border border-n-weak rounded-xl shadow-xl py-1 overflow-hidden"
          :style="cityDropStyle"
          @click.stop
        >
          <button
            v-for="(opt, idx) in filteredCityOptions"
            :key="opt"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
            :class="
              idx === cityHighlight
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : city === opt
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
            "
            @click="selectCity(opt)"
            @mouseenter="cityHighlight = idx"
          >
            {{ opt }}
            <span
              v-if="city === opt"
              class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
            />
          </button>
          <button
            v-if="canCreateCity"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-brand hover:bg-n-alpha-1 transition-colors border-t border-n-weak"
            :class="
              cityHighlight === filteredCityOptions.length
                ? 'bg-n-alpha-1 font-medium'
                : ''
            "
            @click="createAndSelectCity"
            @mouseenter="cityHighlight = filteredCityOptions.length"
          >
            {{
              `+ ${t('KANBAN.LOSS_REASON_MODAL.CREATE_PREFIX')} "${citySearch.trim()}"`
            }}
          </button>
          <p
            v-if="filteredCityOptions.length === 0 && !canCreateCity"
            class="px-3 py-2 text-sm text-n-slate-10 italic"
          >
            {{ t('KANBAN.OPPORTUNITY_MODAL.NOTHING_FOUND') }}
          </p>
        </div>
        <div
          v-if="showCityDropdown"
          class="fixed inset-0 z-[199]"
          @click="showCityDropdown = false"
        />
      </div>
    </div>
    <div class="flex flex-col gap-0.5">
      <span :class="labelClass">
        {{ t('KANBAN.OPPORTUNITY_MODAL.FIELD_SOURCE') }}
      </span>
      <div class="relative" @click.stop>
        <input
          ref="sourceCaptureRef"
          type="text"
          class="sr-only"
          tabindex="-1"
          @input="onSourceInput"
        />
        <button
          ref="sourceTriggerRef"
          class="flex items-center gap-1.5 text-sm w-full text-left outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 min-h-[30px] bg-n-alpha-1 rounded px-2 py-1 transition-colors"
          :class="sourceLocked ? 'cursor-default' : 'hover:bg-n-alpha-2'"
          :title="
            sourceLocked ? t('KANBAN.OPPORTUNITY_MODAL.SOURCE_FROM_UTM') : null
          "
          @click="
            showSourceDropdown
              ? (showSourceDropdown = false)
              : openSourceDropdown()
          "
        >
          <span
            v-if="showSourceDropdown && sourceSearch"
            class="text-n-slate-10 truncate flex-1 leading-none"
            >{{ sourceSearch }}</span
          >
          <span
            v-else
            class="truncate flex-1 leading-none"
            :class="source ? 'text-n-slate-12' : 'text-n-slate-9'"
          >
            {{ source || t('KANBAN.OPPORTUNITY_MODAL.SOURCE_NONE') }}
          </span>
          <span
            class="size-3 text-n-slate-9 flex-shrink-0"
            :class="sourceLocked ? 'i-lucide-lock' : 'i-lucide-chevron-down'"
          />
        </button>
        <div
          v-if="showSourceDropdown"
          class="bg-n-background border border-n-weak rounded-xl shadow-xl py-1 overflow-hidden"
          :style="sourceDropStyle"
          @click.stop
        >
          <button
            v-for="(opt, idx) in filteredSourceOptions"
            :key="opt"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
            :class="
              idx === sourceHighlight
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : source === opt
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
            "
            @click="selectSource(opt)"
            @mouseenter="sourceHighlight = idx"
          >
            {{ opt }}
            <span
              v-if="source === opt"
              class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
            />
          </button>
          <button
            v-if="canCreateSource"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-brand hover:bg-n-alpha-1 transition-colors border-t border-n-weak"
            :class="
              sourceHighlight === filteredSourceOptions.length
                ? 'bg-n-alpha-1 font-medium'
                : ''
            "
            @click="createAndSelectSource"
            @mouseenter="sourceHighlight = filteredSourceOptions.length"
          >
            {{
              `+ ${t('KANBAN.LOSS_REASON_MODAL.CREATE_PREFIX')} "${sourceSearch.trim()}"`
            }}
          </button>
          <p
            v-if="filteredSourceOptions.length === 0 && !canCreateSource"
            class="px-3 py-2 text-sm text-n-slate-10 italic"
          >
            {{ t('KANBAN.OPPORTUNITY_MODAL.NOTHING_FOUND') }}
          </p>
        </div>
        <div
          v-if="showSourceDropdown"
          class="fixed inset-0 z-[199]"
          @click="showSourceDropdown = false"
        />
      </div>
    </div>

    <!-- 4. Pipeline | Etapa -->
    <div class="flex flex-col gap-0.5">
      <span :class="labelClass">
        {{ t('KANBAN.OPPORTUNITY_MODAL.FIELD_PIPELINE') }}
      </span>
      <div class="relative" @click.stop>
        <input
          ref="pipelineCaptureRef"
          type="text"
          class="sr-only"
          tabindex="-1"
          @input="onPipelineInput"
        />
        <button
          ref="pipelineTriggerRef"
          class="flex items-center gap-1.5 text-sm w-full text-left outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 min-h-[30px] bg-n-alpha-1 hover:bg-n-alpha-2 rounded px-2 py-1 transition-colors"
          @click="
            showPipelineDropdown
              ? (showPipelineDropdown = false)
              : openPipelineDropdown()
          "
        >
          <span
            v-if="showPipelineDropdown && pipelineSearch"
            class="text-n-slate-10 truncate flex-1 leading-none"
            >{{ pipelineSearch }}</span
          >
          <span v-else class="text-n-slate-12 truncate flex-1 leading-none">
            {{ currentPipeline?.name || card.pipeline?.name || '—' }}
          </span>
          <span
            class="i-lucide-chevron-down size-3 text-n-slate-9 flex-shrink-0"
          />
        </button>
        <div
          v-if="showPipelineDropdown"
          class="bg-n-background border border-n-weak rounded-xl shadow-xl py-1 overflow-hidden"
          :style="pipelineDropStyle"
          @click.stop
        >
          <button
            v-for="(p, idx) in filteredPipelines"
            :key="p.id"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
            :class="
              idx === pipelineHighlight
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : p.id === pipelineId
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
            "
            @click="selectPipeline(p.id)"
          >
            {{ p.name }}
            <span
              v-if="p.id === pipelineId"
              class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
            />
          </button>
          <p
            v-if="filteredPipelines.length === 0"
            class="px-3 py-2 text-sm text-n-slate-10 italic"
          >
            {{ t('KANBAN.OPPORTUNITY_MODAL.NOTHING_FOUND') }}
          </p>
        </div>
        <div
          v-if="showPipelineDropdown"
          class="fixed inset-0 z-[199]"
          @click="showPipelineDropdown = false"
        />
      </div>
    </div>
    <div class="flex flex-col gap-0.5">
      <span :class="labelClass">
        {{ t('KANBAN.OPPORTUNITY_MODAL.FIELD_STAGE') }}
      </span>
      <div class="relative" @click.stop>
        <input
          ref="stageCaptureRef"
          type="text"
          class="sr-only"
          tabindex="-1"
          @input="onStageInput"
        />
        <button
          ref="stageTriggerRef"
          class="flex items-center gap-1.5 text-sm w-full text-left outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 min-h-[30px] bg-n-alpha-1 hover:bg-n-alpha-2 rounded px-2 py-1 transition-colors"
          @click="
            showStageDropdown
              ? (showStageDropdown = false)
              : openStageDropdown()
          "
        >
          <span
            v-if="showStageDropdown && stageSearch"
            class="text-n-slate-10 truncate flex-1 leading-none"
            >{{ stageSearch }}</span
          >
          <span v-else class="text-n-slate-12 truncate flex-1 leading-none">
            {{ currentStage?.label || '—' }}
          </span>
          <span
            class="i-lucide-chevron-down size-3 text-n-slate-9 flex-shrink-0"
          />
        </button>
        <div
          v-if="showStageDropdown"
          class="bg-n-background border border-n-weak rounded-xl shadow-xl py-1 overflow-hidden"
          :style="stageDropStyle"
          @click.stop
        >
          <button
            v-for="(col, idx) in filteredStages"
            :key="col.value"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
            :class="
              idx === stageHighlight
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : col.value === columnId
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
            "
            @click="selectStage(col.value)"
          >
            {{ col.label }}
            <span
              v-if="col.value === columnId"
              class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
            />
          </button>
          <p
            v-if="filteredStages.length === 0"
            class="px-3 py-2 text-sm text-n-slate-10 italic"
          >
            {{ t('KANBAN.OPPORTUNITY_MODAL.NOTHING_FOUND') }}
          </p>
        </div>
        <div
          v-if="showStageDropdown"
          class="fixed inset-0 z-[199]"
          @click="showStageDropdown = false"
        />
      </div>
    </div>

    <!-- 5. Previsão | Valor -->
    <div class="flex flex-col gap-0.5">
      <span :class="labelClass">
        {{ t('KANBAN.OPPORTUNITY_MODAL.FIELD_FORECAST') }}
      </span>
      <div class="relative group/forecast" @click.stop>
        <button
          ref="forecastTriggerRef"
          class="flex items-center gap-1.5 w-full text-left outline-none focus:outline-none min-h-[30px] bg-n-alpha-1 hover:bg-n-alpha-2 rounded px-2 py-1 transition-colors"
          @click="
            showForecastPicker
              ? (showForecastPicker = false)
              : openForecastPicker()
          "
        >
          <span
            class="text-sm truncate flex-1"
            :class="forecast ? 'text-n-slate-12' : 'text-n-slate-9'"
          >
            {{ formatForecastLabel(forecast, forecastTime) || '—' }}
          </span>
          <span
            class="i-lucide-calendar size-3 text-n-slate-9 opacity-0 group-hover/forecast:opacity-60 transition-opacity flex-shrink-0"
          />
        </button>
        <div
          v-if="showForecastPicker"
          class="fixed inset-0 z-[199]"
          @click="showForecastPicker = false"
        />
        <div v-if="showForecastPicker" :style="forecastPickerStyle">
          <KanbanDatePicker
            :date="forecast"
            :time="forecastTime"
            @update:date="emit('update:forecast', $event)"
            @update:time="emit('update:forecast-time', $event)"
            @close="showForecastPicker = false"
          />
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-0.5">
      <span :class="labelClass">
        {{ t('KANBAN.OPPORTUNITY_MODAL.FIELD_VALUE') }}
      </span>
      <div
        class="relative group/value flex items-center gap-1.5 bg-n-alpha-1 hover:bg-n-alpha-2 rounded px-2 py-1 min-h-[30px] transition-colors"
        :class="editingValue ? 'border-b border-n-brand' : ''"
        @click.stop
      >
        <span class="text-sm text-n-slate-12 flex-shrink-0">{{
          t('KANBAN.OPPORTUNITY_MODAL.CURRENCY_SYMBOL')
        }}</span>
        <input
          ref="valueInputRef"
          :value="displayValue"
          type="text"
          inputmode="numeric"
          :readonly="!editingValue"
          :placeholder="
            editingValue ? t('KANBAN.OPPORTUNITY_MODAL.VALUE_PLACEHOLDER') : '—'
          "
          class="flex-1 text-sm !bg-transparent !outline-none !shadow-none !p-0 !m-0 !h-auto !rounded-none !border-none ring-0 focus:ring-0 w-full appearance-none transition-colors"
          :class="
            editingValue
              ? 'cursor-text text-n-slate-12'
              : displayValue
                ? 'cursor-pointer text-n-slate-12'
                : 'cursor-pointer text-n-slate-9'
          "
          @keydown="onValueKeydown"
          @blur="onValueBlur"
          @click="
            if (!editingValue) {
              editingValue = true;
              $nextTick(() => {
                valueInputRef?.focus();
                valueInputRef?.select();
              });
            }
          "
        />
        <span
          v-if="!editingValue"
          class="i-lucide-pencil size-3 text-n-slate-9 opacity-0 group-hover/value:opacity-60 transition-opacity flex-shrink-0"
        />
      </div>
    </div>

    <!-- 6. Responsável | Produto -->
    <div class="flex flex-col gap-0.5">
      <span :class="labelClass">
        {{ t('KANBAN.OPPORTUNITY_MODAL.FIELD_AGENT') }}
      </span>
      <div class="relative" @click.stop>
        <input
          ref="assigneeCaptureRef"
          type="text"
          class="sr-only"
          tabindex="-1"
          @input="onAssigneeInput"
        />
        <button
          ref="assigneeTriggerRef"
          class="flex items-center gap-2 w-full text-left cursor-pointer select-none outline-none focus:outline-none min-h-[30px] bg-n-alpha-1 hover:bg-n-alpha-2 rounded px-2 py-1 transition-colors"
          @click="assigneeOpen ? (assigneeOpen = false) : openAssignee()"
        >
          <div
            v-if="assigneeSelected"
            class="size-6 rounded-full flex-shrink-0 flex items-center justify-center text-[8px] font-bold text-white overflow-hidden"
            :style="
              assigneeSelected.avatar
                ? {
                    backgroundImage: `url(${assigneeSelected.avatar})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }
                : { backgroundColor: avatarColor(assigneeSelected) }
            "
          >
            <span v-if="!assigneeSelected.avatar">{{
              agentInitials(assigneeSelected.label)
            }}</span>
          </div>
          <span
            class="flex-1 truncate text-sm"
            :class="assigneeSelected ? 'text-n-slate-12' : 'text-n-slate-9'"
          >
            <span
              v-if="assigneeOpen && assigneeSearch"
              class="text-n-slate-10"
              >{{ assigneeSearch }}</span
            >
            <span v-else>{{
              assigneeSelected
                ? assigneeSelected.label
                : t('KANBAN.OPPORTUNITY_MODAL.AGENT_NONE')
            }}</span>
          </span>
          <span
            class="i-lucide-chevron-down size-3 text-n-slate-9 flex-shrink-0"
            :class="{ 'rotate-180': assigneeOpen }"
          />
        </button>
        <div
          v-if="assigneeOpen"
          class="z-[200] bg-n-background border border-n-weak rounded-xl shadow-xl py-1"
          :style="assigneeDropStyle"
          @click.stop
        >
          <button
            v-for="agent in filteredAgents"
            :key="agent.id"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
            :class="
              assigneeNavigable[assigneeHighlight]?.id === agent.id
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : agent.id === assigneeId
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
            "
            @click="selectAssignee(agent.id)"
          >
            <div
              class="size-5 rounded-full flex-shrink-0 flex items-center justify-center text-[7.5px] font-bold text-white overflow-hidden"
              :style="
                agent.avatar
                  ? {
                      backgroundImage: `url(${agent.avatar})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }
                  : { backgroundColor: avatarColor(agent) }
              "
            >
              <span v-if="!agent.avatar">{{ agentInitials(agent.label) }}</span>
            </div>
            <span class="truncate">{{ agent.label }}</span>
            <span
              v-if="agent.id === assigneeId"
              class="i-lucide-check size-3 text-n-brand ml-auto flex-shrink-0"
            />
          </button>
          <p
            v-if="assigneeSearch && !filteredAgents.length"
            class="px-3 py-2 text-sm text-n-slate-10 italic"
          >
            {{ t('KANBAN.OPPORTUNITY_MODAL.NOTHING_FOUND') }}
          </p>
          <div class="border-t border-n-weak my-1" />
          <button
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
            :class="
              assigneeNavigable[assigneeHighlight]?.id === 'none'
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : !assigneeId
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-11'
            "
            @click="selectAssignee('none')"
          >
            <span class="i-lucide-user-x size-3.5 flex-shrink-0" />
            {{ t('KANBAN.OPPORTUNITY_MODAL.AGENT_NONE') }}
          </button>
        </div>
        <div
          v-if="assigneeOpen"
          class="fixed inset-0 z-20"
          @click="assigneeOpen = false"
        />
      </div>
    </div>

    <div class="flex flex-col gap-0.5">
      <span :class="labelClass">
        {{ t('KANBAN.OPPORTUNITY_MODAL.FIELD_PRODUCT') }}
      </span>
      <div class="relative" @click.stop>
        <input
          ref="productCaptureRef"
          type="text"
          class="sr-only"
          tabindex="-1"
          @input="onProductInput"
        />
        <button
          ref="productTriggerRef"
          class="flex items-center gap-1.5 text-sm w-full text-left outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 min-h-[30px] bg-n-alpha-1 hover:bg-n-alpha-2 rounded px-2 py-1 transition-colors"
          @click="
            showProductDropdown
              ? (showProductDropdown = false)
              : openProductDropdown()
          "
        >
          <span
            v-if="showProductDropdown && productSearch"
            class="text-n-slate-10 truncate flex-1 leading-none"
            >{{ productSearch }}</span
          >
          <span
            v-else
            class="truncate flex-1 leading-none"
            :class="productChipLabel ? 'text-n-slate-12' : 'text-n-slate-9'"
          >
            {{ productChipLabel || t('KANBAN.OPPORTUNITY_MODAL.PRODUCT_NONE') }}
          </span>
          <span
            class="i-lucide-chevron-down size-3 text-n-slate-9 flex-shrink-0"
          />
        </button>
        <div
          v-if="showProductDropdown"
          class="bg-n-background border border-n-weak rounded-xl shadow-xl py-1 overflow-hidden"
          :style="productDropStyle"
          @click.stop
        >
          <button
            v-for="(opt, idx) in filteredProductOptions"
            :key="opt"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
            :class="
              idx === productHighlight
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : products.includes(opt)
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
            "
            @click="toggleProduct(opt)"
            @mouseenter="productHighlight = idx"
          >
            {{ opt }}
            <span
              v-if="products.includes(opt)"
              class="i-lucide-check size-3 ml-auto text-n-brand flex-shrink-0"
            />
          </button>
          <button
            v-if="canCreateProduct"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-brand hover:bg-n-alpha-1 transition-colors border-t border-n-weak"
            :class="
              productHighlight === filteredProductOptions.length
                ? 'bg-n-alpha-1 font-medium'
                : ''
            "
            @click="createAndToggleProduct"
            @mouseenter="productHighlight = filteredProductOptions.length"
          >
            {{
              `+ ${t('KANBAN.LOSS_REASON_MODAL.CREATE_PREFIX')} "${productSearch.trim()}"`
            }}
          </button>
          <p
            v-if="filteredProductOptions.length === 0 && !canCreateProduct"
            class="px-3 py-2 text-sm text-n-slate-10 italic"
          >
            {{ t('KANBAN.OPPORTUNITY_MODAL.NOTHING_FOUND') }}
          </p>
        </div>
        <div
          v-if="showProductDropdown"
          class="fixed inset-0 z-[199]"
          @click="showProductDropdown = false"
        />
      </div>
    </div>

    <!-- 7. Seguidores | Tags -->
    <div class="flex flex-col gap-0.5">
      <span :class="labelClass">{{
        t('KANBAN.OPPORTUNITY_MODAL.FIELD_WATCHERS')
      }}</span>
      <div class="relative" @click.stop>
        <input
          ref="watchersCaptureRef"
          type="text"
          class="sr-only"
          tabindex="-1"
          @input="onWatchersInput"
        />
        <button
          ref="watchersTriggerRef"
          class="flex items-center gap-1 w-full text-left outline-none focus:outline-none min-h-[24px] p-0"
          @click="watchersOpen ? (watchersOpen = false) : openWatchers()"
        >
          <div v-if="watcherAgents.length" class="flex items-center">
            <div
              v-for="(agent, idx) in watcherAgents"
              :key="agent.id"
              class="size-6 rounded-full flex-shrink-0 flex items-center justify-center text-[8px] font-bold text-white overflow-hidden border border-n-background"
              :class="idx > 0 ? '-ml-1.5' : ''"
              :style="
                agent.avatar
                  ? {
                      backgroundImage: `url(${agent.avatar})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }
                  : { backgroundColor: avatarColor(agent) }
              "
            >
              <span v-if="!agent.avatar">{{ agentInitials(agent.label) }}</span>
            </div>
            <span
              class="i-lucide-user-plus size-3.5 text-n-slate-9 ml-1.5 flex-shrink-0"
            />
          </div>
          <span v-else class="i-lucide-user-plus size-4 text-n-slate-9" />
        </button>
        <div
          v-if="watchersOpen"
          class="z-[200] bg-n-background border border-n-weak rounded-xl shadow-xl py-1"
          :style="watchersDropStyle"
          @click.stop
        >
          <button
            v-for="agent in filteredWatcherAgents"
            :key="agent.id"
            class="w-full flex items-center justify-start gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors text-left"
            :class="
              watchersNavigable[watchersHighlight]?.id === agent.id
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : props.watcherIds.includes(agent.id)
                  ? 'text-n-brand font-medium'
                  : agent.id === props.assigneeId
                    ? 'text-n-slate-10'
                    : 'text-n-slate-12'
            "
            @click="toggleWatcher(agent.id)"
          >
            <div
              class="size-5 rounded-full flex-shrink-0 flex items-center justify-center text-[7.5px] font-bold text-white overflow-hidden"
              :style="
                agent.avatar
                  ? {
                      backgroundImage: `url(${agent.avatar})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }
                  : { backgroundColor: avatarColor(agent) }
              "
            >
              <span v-if="!agent.avatar">{{ agentInitials(agent.label) }}</span>
            </div>
            <span class="truncate flex-1">{{ agent.label }}</span>
            <span
              v-if="agent.id === props.assigneeId"
              class="text-[10px] text-n-slate-9 ml-auto flex-shrink-0 italic"
              >{{ t('KANBAN.OPPORTUNITY_MODAL.ASSIGNEE_ROLE_LABEL') }}</span
            >
            <span
              v-else-if="props.watcherIds.includes(agent.id)"
              class="i-lucide-check size-3 text-n-brand ml-auto flex-shrink-0"
            />
          </button>
          <p
            v-if="watchersSearch && !filteredWatcherAgents.length"
            class="px-3 py-2 text-sm text-n-slate-10 italic"
          >
            {{ t('KANBAN.OPPORTUNITY_MODAL.NOTHING_FOUND') }}
          </p>
          <div class="border-t border-n-weak my-1" />
          <button
            class="w-full flex items-center justify-start gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors text-left"
            :class="
              watchersNavigable[watchersHighlight]?.id === 'clear'
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : !props.watcherIds.length
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-11'
            "
            @click="
              emit('update:watcher-ids', []);
              watchersOpen = false;
            "
          >
            <span class="i-lucide-user-x size-3.5 flex-shrink-0" />
            {{ t('KANBAN.OPPORTUNITY_MODAL.WATCHERS_NONE') }}
          </button>
        </div>
        <div
          v-if="watchersOpen"
          class="fixed inset-0 z-20"
          @click="watchersOpen = false"
        />
        <!-- Confirmação: mover responsável para seguidor -->
        <div
          v-if="watchersMoveConfirmId"
          class="fixed inset-0 z-[300] flex items-center justify-center"
          @click.self="watchersMoveConfirmId = null"
        >
          <div
            class="bg-n-background border border-n-weak rounded-xl shadow-xl p-4 w-72 flex flex-col gap-3"
            @click.stop
          >
            <p class="text-sm text-n-slate-12 leading-snug">
              {{ t('KANBAN.OPPORTUNITY_MODAL.WATCHERS_MOVE_CONFIRM') }}
            </p>
            <div class="flex gap-2 justify-end">
              <button
                class="px-3 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
                @click="watchersMoveConfirmId = null"
              >
                {{ t('KANBAN.OPPORTUNITY_MODAL.WATCHERS_MOVE_CANCEL') }}
              </button>
              <button
                class="px-3 py-1.5 text-sm rounded-lg bg-n-brand text-white hover:brightness-110 transition-all"
                @click="confirmMoveAssigneeToWatcher"
              >
                {{ t('KANBAN.OPPORTUNITY_MODAL.WATCHERS_MOVE_CONFIRM_BTN') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-0.5">
      <span :class="labelClass">
        {{ t('KANBAN.OPPORTUNITY_MODAL.FIELD_TAGS') }}
      </span>
      <div class="flex items-center gap-1 flex-wrap">
        <span
          v-for="tag in tags"
          :key="tag"
          class="group/tag inline-flex items-center h-5 text-[11px] leading-none font-medium px-1.5 rounded shrink-0"
          :style="{
            backgroundColor: tagColor(tag).bg,
            color: tagColor(tag).text,
          }"
        >
          {{ tag }}
          <span
            class="overflow-hidden transition-all duration-150 max-w-0 group-hover/tag:max-w-[12px] flex items-center justify-center"
          >
            <button
              class="flex items-center justify-center hover:bg-black/10 rounded-full ml-[2px]"
              @click="removeTag(tag, $event)"
            >
              <span class="i-lucide-x size-[8px] flex-shrink-0" />
            </button>
          </span>
        </span>
        <div class="relative shrink-0" @click.stop>
          <input
            ref="tagCaptureRef"
            type="text"
            class="sr-only"
            tabindex="-1"
            @input="onTagInput"
          />
          <button
            ref="tagTriggerRef"
            class="opacity-60 hover:opacity-100 transition-opacity focus:outline-none"
            :class="showTagMenu && tagSearch ? 'opacity-100' : ''"
            @click="openTagMenu"
          >
            <span
              v-if="showTagMenu && tagSearch"
              class="text-[9px] font-medium text-n-slate-10"
              >{{ tagSearch }}</span
            >
            <span v-else class="i-lucide-tag size-3 text-n-slate-10" />
          </button>
          <div
            v-if="showTagMenu"
            class="fixed inset-0 z-[199]"
            @click="showTagMenu = false"
          />
          <div
            v-if="showTagMenu"
            class="bg-n-background border border-n-weak rounded-xl shadow-xl w-44 overflow-hidden py-1"
            :style="tagMenuStyle"
            @click.stop
          >
            <button
              v-for="(lbl, idx) in filteredAccountLabels"
              :key="lbl.id"
              class="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-n-alpha-1 transition-colors focus:outline-none"
              :class="
                idx === tagHighlight
                  ? 'bg-n-alpha-1 text-n-slate-12 font-medium'
                  : isTagActive(lbl.title)
                    ? 'text-n-slate-12 font-medium'
                    : 'text-n-slate-11'
              "
              @click="toggleTag(lbl.title)"
              @mouseenter="tagHighlight = idx"
            >
              <span
                class="size-2.5 rounded-full flex-shrink-0"
                :style="{ backgroundColor: lbl.color }"
              />
              <span class="truncate">{{ lbl.title }}</span>
              <span
                v-if="isTagActive(lbl.title)"
                class="i-lucide-check size-3 ml-auto flex-shrink-0 text-n-brand"
              />
            </button>
            <p
              v-if="filteredAccountLabels.length === 0"
              class="px-3 py-2 text-xs text-n-slate-10 italic text-center"
            >
              {{ t('TASKS.TAGS.EMPTY') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
